//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { createContext } from 'react';

import { type PagePublication, type UiAction } from './contract';

export type ActionHandler = (action: UiAction) => void;

// The bridge between pages and the agent panel.  A page publishes what the
// user is looking at and registers handlers for the actions it can apply;
// the panel reads the snapshot at send time and dispatches actions through
// the handlers.  Neither side imports the other.  See
// docs/design/agent-runtime.md, "Frontend".
//
// A store read through getters rather than React state: nothing renders from
// the snapshot, the transport reads it at send time, so a page republishing
// on every keystroke must not re-render the panel.  The getters always
// answer with the current value, which is what lets a dispatch that follows
// a navigation see the handlers the arriving page registers.
export interface PageSnapshotContextValue {
  getPublished: () => PagePublication | null;
  // Returns the unpublish function, which clears the snapshot only while it
  // is still the one published: a page mounting in the same commit as the
  // one leaving must not lose its snapshot to the old page's cleanup.
  publish: (publication: PagePublication) => () => void;
  getHandlers: () => ReadonlyMap<string, ActionHandler>;
  // Returns the unregister function, guarded the same way.
  registerHandler: (key: string, handler: ActionHandler) => () => void;
  // Called after every publish, unpublish, register, or unregister.  Returns
  // the unsubscribe function.
  subscribe: (listener: () => void) => () => void;
}

export const PageSnapshotContext =
  createContext<PageSnapshotContextValue | null>(null);
